import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import './NotificationSidebar.css';
import { X, CheckCircle, Package, Loader2, Info, Bell, AlertTriangle } from 'lucide-react';
import { farmvestService } from '../../services/farmvest_api';
import { markAllRead } from '../../store/slices/wsSlice';
import type { RootState, AppDispatch } from '../../store';

/** Unified shape used for rendering — covers both API history and WS real-time items */
interface DisplayNotification {
  id: string;
  title: string;
  body: string;
  type: string;          // notification_type from API, or 'notification'/'iot_alert' from WS
  referenceId?: number;
  isRead: boolean;
  timestamp: string;     // ISO string
  source: 'api' | 'ws'; // where this came from
}

interface ApiNotificationItem {
  id: number;
  title: string;
  body: string;
  notification_type: string;
  reference_id: number;
  is_read: boolean;
  created_at: string;
}

interface NotificationSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  onAllRead?: () => void;
}

const NotificationSidebar: React.FC<NotificationSidebarProps> = ({ isOpen, onClose, onAllRead }) => {
  const dispatch = useDispatch<AppDispatch>();
  const wsNotifications = useSelector((s: RootState) => s.ws.notifications);

  const [apiNotifications, setApiNotifications] = useState<DisplayNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  // Snapshot of WS notifications captured at the moment the sidebar opens.
  // We freeze them so that new arrivals while the sidebar is open don't jump into the list.
  const [wsSnapshot, setWsSnapshot] = useState<DisplayNotification[]>([]);

  // Track which API ids we already have so we can deduplicate against WS snapshot
  const apiIdsRef = useRef<Set<string>>(new Set());

  const fetchNotifications = async (pageNum: number) => {
    try {
      if (pageNum === 1) setLoading(true);
      const res = await farmvestService.getNotificationsHistory(pageNum, 20);

      if (res && res.status === 'success') {
        const items: DisplayNotification[] = (res.data || []).map((n: ApiNotificationItem) => {
          const id = String(n.id);
          apiIdsRef.current.add(id);
          return {
            id,
            title: n.title,
            body: n.body,
            type: n.notification_type,
            referenceId: n.reference_id,
            isRead: n.is_read,
            timestamp: n.created_at,
            source: 'api' as const,
          };
        });

        if (pageNum === 1) {
          setApiNotifications(items);
        } else {
          setApiNotifications(prev => [...prev, ...items]);
        }

        if (res.pagination) {
          setHasMore(res.pagination.current_page < res.pagination.total_pages);
        } else {
          setHasMore(false);
        }
      }
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    } finally {
      if (pageNum === 1) setLoading(false);
    }
  };

  const markAllReadEverywhere = async () => {
    try {
      await farmvestService.markNotificationsRead({ mark_all: true });
      setApiNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      dispatch(markAllRead());
      onAllRead?.();
    } catch (error) {
      console.error("Failed to mark notifications as read:", error);
    }
  };

  useEffect(() => {
    if (isOpen) {
      // Reset state for fresh open
      apiIdsRef.current = new Set();
      setPage(1);
      setApiNotifications([]);

      // Freeze current WS notifications as snapshot (unread ones bubble to top)
      const snapshot = wsNotifications
        .filter(n => !n.isRead)
        .map(n => ({
          id: n.id,
          title: n.title,
          body: n.body,
          type: n.type,
          referenceId: n.referenceId,
          isRead: false,
          timestamp: n.timestamp,
          source: 'ws' as const,
        }));
      setWsSnapshot(snapshot);

      fetchNotifications(1).then(() => markAllReadEverywhere());
    } else {
      // Clear snapshot when closed so stale items don't linger
      setWsSnapshot([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const loadMore = () => {
    if (!loading && hasMore) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchNotifications(nextPage);
    }
  };

  /**
   * Merge WS snapshot (top) with API history (below), deduplicating:
   * If a WS item's id matches an API item (backend sends the DB row id via WS),
   * the API version supersedes — we drop the WS copy since the API already has it.
   */
  const mergedNotifications: DisplayNotification[] = [
    ...wsSnapshot.filter(ws => !apiIdsRef.current.has(ws.id)),
    ...apiNotifications,
  ];

  const getIcon = (type: string) => {
    const t = type.toLowerCase();
    if (t === 'payment') {
      return <div className="notif-icon payment"><CheckCircle size={18} /></div>;
    }
    if (t === 'order') {
      return <div className="notif-icon order"><Package size={18} /></div>;
    }
    if (t === 'iot_alert') {
      return <div className="notif-icon iot"><AlertTriangle size={18} /></div>;
    }
    return <div className="notif-icon default"><Bell size={18} /></div>;
  };

  const formatDate = (dateString: string) => {
    try {
      if (!dateString) return '';

      // Parse the date. The backend seems to send local time strings. 
      // Forcing 'Z' was causing timezone mismatches.
      const date = new Date(dateString);
      const now = new Date();

      // Calculate difference in seconds
      const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

      // Handle clock skew or future dates
      if (diffInSeconds < 30) {
        return 'Just now';
      }

      if (diffInSeconds < 60) {
        return 'Less than a minute ago';
      }

      if (diffInSeconds < 3600) {
        const minutes = Math.floor(diffInSeconds / 60);
        return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
      }

      if (diffInSeconds < 86400) {
        const hours = Math.floor(diffInSeconds / 3600);
        // Check if it was actually today
        if (date.getDate() === now.getDate()) {
          return `${hours} hour${hours > 1 ? 's' : ''} ago`;
        }
      }

      // For older notifications, show consistent date format
      const formattedDate = date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
      });

      const formattedTime = date.toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });

      return `${formattedDate}, ${formattedTime}`;
    } catch (err) {
      console.error("Date parsing error:", err);
      return dateString;
    }
  };

  return (
    <>
      <div className={`notif-sidebar-overlay ${isOpen ? 'open' : ''}`} onClick={onClose} />
      <div className={`notif-sidebar ${isOpen ? 'open' : ''}`}>
        <div className="notif-sidebar-header">
          <h2>Notifications</h2>
          <button className="notif-close-btn" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="notif-sidebar-content">
          {loading && page === 1 ? (
            <div className="notif-loading">
              <Loader2 className="spinner" size={32} />
              <p>Loading notifications...</p>
            </div>
          ) : mergedNotifications.length === 0 ? (
            <div className="notif-empty">
              <div className="empty-icon"><Info size={48} /></div>
              <p>No notifications yet</p>
            </div>
          ) : (
            <div className="notif-list">
              {mergedNotifications.map((notif) => (
                <div key={`${notif.source}-${notif.id}`} className={`notif-item ${!notif.isRead ? 'unread' : ''}`}>
                  {getIcon(notif.type)}
                  <div className="notif-details">
                    <div className="notif-title-row">
                      <h4>{notif.title}</h4>
                      <span className="notif-time">{formatDate(notif.timestamp)}</span>
                    </div>
                    <p className="notif-body">{notif.body}</p>
                  </div>
                </div>
              ))}

              {hasMore && (
                <button className="notif-load-more" onClick={loadMore} disabled={loading}>
                  {loading ? <Loader2 className="spinner" size={16} /> : 'Load More'}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default NotificationSidebar;
